import { inputClass } from '../AuthCard.tsx';
import type { EditProps, ViewProps } from './types.ts';

export function DataTableView({ content }: ViewProps<'table'>) {
  if (content.rows.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {content.columns.map((col, i) => (
              <th
                key={i}
                className="border-b-2 border-gray-300 px-3 py-2 text-left font-semibold text-gray-600"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {content.rows.map((row, ri) => (
            <tr key={ri} className="break-inside-avoid">
              {row.map((cell, ci) => (
                <td key={ci} className="border-b border-gray-200 px-3 py-2 text-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DataTableEdit({ content, onChange }: EditProps<'table'>) {
  const setColumn = (i: number, value: string) =>
    onChange({ ...content, columns: content.columns.map((c, j) => (j === i ? value : c)) });
  const setCell = (ri: number, ci: number, value: string) =>
    onChange({
      ...content,
      rows: content.rows.map((row, i) =>
        i === ri ? row.map((cell, j) => (j === ci ? value : cell)) : row,
      ),
    });
  const addRow = () =>
    onChange({ ...content, rows: [...content.rows, content.columns.map(() => '')] });
  const removeRow = (ri: number) =>
    onChange({ ...content, rows: content.rows.filter((_, i) => i !== ri) });
  const addColumn = () =>
    onChange({
      ...content,
      columns: [...content.columns, ''],
      rows: content.rows.map((row) => [...row, '']),
    });
  const removeColumn = (ci: number) =>
    onChange({
      ...content,
      columns: content.columns.filter((_, j) => j !== ci),
      rows: content.rows.map((row) => row.filter((_, j) => j !== ci)),
    });

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {content.columns.map((col, ci) => (
                <th key={ci} className="p-1">
                  <div className="flex items-center gap-1">
                    <input
                      className={`${inputClass} font-semibold`}
                      placeholder={`Column ${ci + 1}`}
                      value={col}
                      onChange={(e) => setColumn(ci, e.target.value)}
                    />
                    {content.columns.length > 1 && (
                      <button
                        type="button"
                        className="text-xs text-red-500 hover:underline"
                        title="Remove column"
                        onClick={() => removeColumn(ci)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th className="w-16 p-1" />
            </tr>
          </thead>
          <tbody>
            {content.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="p-1">
                    <input
                      className={inputClass}
                      value={cell}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                    />
                  </td>
                ))}
                <td className="p-1">
                  <button
                    type="button"
                    className="text-sm text-red-500 hover:underline"
                    onClick={() => removeRow(ri)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4">
        <button type="button" className="text-sm text-blue-600 hover:underline" onClick={addRow}>
          Add row
        </button>
        <button type="button" className="text-sm text-blue-600 hover:underline" onClick={addColumn}>
          Add column
        </button>
      </div>
    </div>
  );
}
